import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import { formatDate } from '@angular/common';
import { tap } from 'rxjs';

import { ContractExtensionsCountersService, ContractExtensionsService, NotificationService, OverviewTableService, WebsocketService } from '@shared/services';
import { TableBodyEntryModel, TableHeaderEntryModel } from '@shared/models';
import { ContractExtension, Employee } from '@shared/factories';

@Component({
  selector:     'contract-ext-table',
  templateUrl: '../../table-entries.component.html',
  styleUrls: ['./contract-extensions-table.component.sass'],
  host: { class: 'am-flex-column bg-white b-radius-wide overflow' },
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ContractExtensionsTableComponent {
  @Input() rows:         ContractExtension[];
  @Input() paginationId: string;
  @Input() showArchiv:   boolean = true;
  @Input() loading:      boolean;

  @Output() headerCallback = new EventEmitter<any>();
  @Output() updateData     = new EventEmitter<any>();

  constructor(
    private contractExtensionsCounter: ContractExtensionsCountersService,
    private contractExtensionsService: ContractExtensionsService,
    private websocketService:          WebsocketService,
    private overviewTableService:      OverviewTableService,
    private notificationService:       NotificationService
  ) { }

  headerCallbackHandler(res) {
    this.headerCallback.emit(res);
  }

  getTableClass(): string {
    return 'contract-extensions-list-wrapper';
  }

  getOptionalValue(field: string): boolean {
    return this[field] || null;
  }

  prepareTableHeader(): TableHeaderEntryModel[] {
    return [
      { title: 'Mitarbeiter',      class: 'name-cell'                                                                 },
      { title: 'Eingang',          class: 'created-cell',   sort_by: ['created_at']                                   },
      { title: 'Einsatz',          class: 'title-cell'                                                                },
      { title: 'Kunde',            class: 'customer-cell'                                                             },
      { title: 'Verlängerung bis', class: 'renew-till-cell'                                                           },
      { title: 'Archiviert',       class: 'archived-cell',  sort_by: ['archived_at'], pageOnly: ['archive']           },
      { title: 'Aktion',           class: 'action-cell',                              pageOnly: ['contract-ext-list'] }
    ].filter(h => h.pageOnly ? h.pageOnly.find(p => p === this.paginationId) : true);
  }

  private preparePreload(): TableBodyEntryModel[] {
    return new Array<TableBodyEntryModel>(10).fill({
      cells: ([
        this.overviewTableService.getExternalEmployeeCellPlaceholder(),
        { xs_label: ' ',  class: 'created-cell',       value: ' '                        },
        ...this.overviewTableService.getAssignmentCellsPlaceholder(),
        { xs_label: ' ',  class: 'customer-rep-cell',  value: ' '                        },
        { xs_label: ' ',  class: 'customer-cell-cell', children: [
          { value: ' ' },
          { value: ' ' },
        ]},
        { xs_label: ' ',  class: 'renew-till-cell',    value: ' '                        },
        { xs_label: ' ',  class: `archived-cell`,      value: ' ', pageOnly: ['archive'] },
        { xs_label: null, class: 'action-cell',                    pageOnly: ['contract-ext-list'], buttons: [
          { class: 'am-flex height-xl width-xl loading-animate xs-hide' }, { class: 'custom-action-cell-button xs-show-table' }]
        }
      ])
    });
  }

  prepareTableBody(): TableBodyEntryModel[] {
    if (this.loading) return this.preparePreload();
    return this.rows.map((model: ContractExtension): TableBodyEntryModel => ({
      cells: [
        this.overviewTableService.getExternalEmployeeCell(new Employee(Object.assign(model.assignment?.external_employee.toJSON(), { personal_number: model.assignment.personal_number }))),
        { xs_label: 'Eingang',          class: 'created-cell',      value: formatDate(model.created_at, 'dd.MM.yyyy', 'de')                                                                   },
        ...this.overviewTableService.getAssignmentCells( model.assignment),
        { xs_label: 'Ansprechpartner',  class: 'customer-rep-cell', value: `${model.customer.first_name} ${model.customer.last_name}`                                                         },
        { xs_label: 'Kunde',            class: 'customer-cell am-flex-row', children: [
          { class: 'am-flex-row am-flex-center', value: `${model.customerCompany?.name} (${model.customerCompany?.company_number})` },
          { class: 'web-tooltip bottom short blue', children: [{
              class: 'tooltip-title',
              value: `Ansprechpartner: ${model.customer.first_name} ${model.customer.last_name}`,
          }]}
        ]},
        { xs_label: 'Verlängerung bis', class: 'renew-till-cell',   value: formatDate(model.contract_extension_till, 'dd.MM.yyyy', 'de')                                                      },
        { xs_label: 'Archiviert',       class: `archived-cell`,     value: formatDate(model.archived_at,             'dd.MM.yyyy', 'de'), pageOnly: ['archive']                               },
        { xs_label: null,               class: 'action-cell',                                                                             pageOnly: ['contract-ext-list'],
          buttons: [
            {
              class:   'font-small-icon tooltip-hover blue-thin-border xs-hide',
              icon:    'icon-archive am-flex-center m0',
              tooltip: 'Archivieren',
              click: () => this.manualArchive(model)
            },
            {
              class: 'blue-thin-border custom-action-cell-button xs-show-table',
              icon:  'icon-archive m0',
              label: 'Archivieren',
              click: () => this.manualArchive(model)
            }
          ]
        }
      ].filter(c => c.pageOnly && c.pageOnly.filter(p => p).length ? c.pageOnly.filter(p => p).find(p => p === this.paginationId) : true)
    }));
  }

  private manualArchive(model: ContractExtension): void {
    this.notificationService.confirm('Bestätigung', 'Möchten Sie diese Einsatzverlängerung archivieren ?', () => {
      this.notificationService.wait();
      this.contractExtensionsService.archiveContractExtension(model.id).pipe(
        tap(() => this.refreshData())
      ).subscribe(
        () => this.notificationService.close(),
        error => this.notificationService.alert(error.message || error)
      );
    });
  }

  private refreshData(): void {
    this.updateData.emit();
    this.reloadCounters();
  }

  private reloadCounters(): void {
    if (!this.websocketService.wsOnline()) this.contractExtensionsCounter.reloadCounters();
  }

}
